
/// <amd-module name="Environments/CreatePanel/Components/AddResourceComponent" />
import * as React from "react";

import { EnvironmentResourceReference, EnvironmentResourceType, EnvironmentResource } from "DistributedTask/Client/Contracts/TaskAgent";

import * as Resources from "Environments/CreatePanel/Resources";

import { getTitle, getResources, IResource, PageKey } from "Environments/CreatePanel/Common/Utils";
import { EnvironmentBaseComponent, IEnvironmentBaseComponentProperties, IEnvironmentBaseComponentState } from "Environments/EnvironmentsCommon/EnvironmentBaseComponent";
import { IResourceContributionProps, IResourceCreatedResponse } from "Environments/EnvironmentsCommon/Utils";

import { IVssComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { IButtonProps } from "VSSUI/Button";
import { Panel, IPanelTitleProps } from "VSSUI/Panel";
import { Spinner, SpinnerSize } from 'VSSUI/Spinner';
import { format } from "VSS/Core/Util/String";
import { Tooltip } from "VSSUI/TooltipEx";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";
import { Area, FeatureNames, Action } from "Environments/CreatePanel/Common/Utils";

interface IAddResourceState extends IEnvironmentBaseComponentState {
    disableActionButton: boolean;
    showButtons: boolean;
    forwardButtonText: string;
    backButtonText: string;
}

interface IAddResourceProperties extends IEnvironmentBaseComponentProperties {
    onResourceCreated: () => void;
    onResourceCreationFailed: () => void;
    onDismissPanel: () => void;
    onCreateButtonClick: () => void;
    onBackButtonClick?: () => void;
    instanceId: string;
    resourceType: EnvironmentResourceType | undefined;
    environmentId: number | undefined | null;
    environmentName: string | undefined;
    isAddEnvironmentView: boolean;
    isEdit?: boolean;
    resource?: EnvironmentResource;
    properties?: { [key: string]: any }
    createResourceSignal: boolean;
    urlToCurrentView?: string;
}

const LoadingComponent = () => (
    <Spinner
        label={Resources.Loading}
        size={SpinnerSize.large}
        className="absolute-fill"
    />
);

export class AddResourceComponent extends EnvironmentBaseComponent<IAddResourceProperties, IAddResourceState> {

    public getInstanceId(context: IVssComponentContext): string {
        return this.props.instanceId;
    }

    constructor(props: IAddResourceProperties, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            errorMessages: {},
            disableActionButton: true,
            backButtonText: Resources.BackButtonText,
            forwardButtonText: Resources.CreateButtonText,
            showButtons: true
        };
    }

    public render(): JSX.Element {
        const providerCreateContributionId: string = this._getCreateContributionId();
        const resourceProps: IResourceContributionProps = {
            parentInstanceId: this._instanceId,
            environmentId: this.props.environmentId,
            onCreateButtonStatusUpdate: this._onCreateButtonStatusUpdate,
            onResourceCreated: {
                onSuccess: this._onSuccessfullyResourceCreated,
                onError: this._onResourceCreationFailed
            } as IResourceCreatedResponse,
            environmentName: this.props.environmentName,
            createResourceSignal: this.props.createResourceSignal,
            urlToCurrentView: this.props.urlToCurrentView,
            isEditMode: this.props.isEdit,
            resource: this.props.resource,
            properties: this.props.properties,
            footerButtonCallbacks: {
                onCreateButtonStatusUpdate: this._onCreateButtonStatusUpdate,
                setBackButtonText: this._setBackButtonText,
                setForwardButtonText: this._setForwardButtonText,
                updateButtonVisibility: this._updateButtonVisibility
            },
            headerClassName: "title-xs no-margin env-panel-header"
        };

        return (
            <Panel
                titleProps={this._getTitle()}
                onDismiss={this.props.onDismissPanel}
                footerButtonProps={this._getButtonProps()}
            >
                <div className="panel-content flex-grow">
                    {this.getErrorMessageView(PageKey.AddEnvironmentView)}
                    {this.getErrorMessageView(PageKey.AddResourceView)}
                    <WrappedComponent
                        wrappedType={this._getSectionName()}
                        wrappedProps={resourceProps}
                        dependencies={[providerCreateContributionId]}
                        className="flex-cell flex-grow justify-center fontSizeL"
                        loadingComponent={LoadingComponent}
                    />
                </div>
            </Panel>
        );
    }

    private _getTitle(): IPanelTitleProps {
        if (this.props.isEdit) {
            return getTitle((this.props.isEdit && this.props.resource) ? this.props.resource.name : Resources.EditResource);
        }
        else if (this.props.isAddEnvironmentView) {
            return getTitle(Resources.NewEnvironment);
        } else {
            return getTitle(Resources.AddResource);
        }
    }

    private _getCreateContributionId(): string {
        const provider = this._getProviderDetails();
        return provider ? provider.providerCreateContributionId : "";
    }

    private _getSectionName(): string {
        const provider = this._getProviderDetails();
        return provider ? provider.componentType : "";
    }

    private _getProviderDetails(): IResource | undefined {
        return getResources(this.context.pageContext).find((resource: IResource) => {
            return resource.resourceType === this.props.resourceType
        });
    }

    private _onSuccessfullyResourceCreated = (resource: EnvironmentResourceReference | undefined | null): void => {
        this._publishResourceTypeTelemtry(resource);
        if (resource || this.props.isEdit) {
            this.state.errorMessages[PageKey.AddResourceView] = undefined;
            this.props.onResourceCreated();
        }
    }


    private _publishResourceTypeTelemtry(resource: EnvironmentResourceReference | undefined | null) {
        if (resource && !this.props.isEdit) {
            const environmentId = this.props.environmentId && this.props.environmentId.toString() || "";
            const environmentName = this.props.environmentName || "";
            // Need to remove after 1-2 sprint
            publishTelemetry(this.context.pageContext, Area, FeatureNames.EnvironmentCreateWithResourceType, { "EnvironmentId": environmentId, "EnvironmentName": environmentName, "ResourceType": resource.type.toString(), "IsEdit": this.props.isEdit ? "True" : "False" });
            publishTelemetry(this.context.pageContext, Area, FeatureNames.EnvironmentCreationWorkFlow, { "Action": Action.EnvironmentCreateWithResourceType, "EnvironmentId": environmentId, "ResourceType": resource.type.toString(), "IsEdit": this.props.isEdit ? "true" : "false" });
        }
    }

    private _onResourceCreationFailed = (errorMessage: string | undefined | null): void => {
        if (this.props.isAddEnvironmentView && this.props.resourceType === EnvironmentResourceType.Kubernetes) {
            const formatedErrorMessage = format(Resources.ResourceCreationFailedMessage, this.props.environmentName, errorMessage);
            this.state.errorMessages[PageKey.AddResourceView] = this._getMessageArea(formatedErrorMessage);
            this.setState({ errorMessages: this.state.errorMessages });
        } else if (this.props.isAddEnvironmentView) {
            const formatedErrorMessage = format(Resources.ResourceCreationFailedMessage, this.props.environmentName, errorMessage);
            this.state.errorMessages[PageKey.AddResourceView] = formatedErrorMessage;
            this.setState({ errorMessages: this.state.errorMessages });
        } else {
            this.state.errorMessages[PageKey.AddResourceView] = errorMessage;
            this.setState({ errorMessages: this.state.errorMessages });
        }
        this.props.onResourceCreationFailed();
    }

    private _getMessageArea(message: string): React.ReactNode {
        const messageElement = message ? React.createElement(Tooltip, { text: message },
            React.createElement("div", { className: "k8s-resource-message" }, message)) : null;
        return messageElement;
    }

    private _getButtonProps(): IButtonProps[] | undefined {
        if (!this.state.showButtons) {
            return undefined;
        }
        
        let buttonsProps: IButtonProps[] = [];
        if (this.props.onBackButtonClick) {
            buttonsProps.push({
                ariaLabel: this.state.backButtonText || Resources.BackButtonText,
                onClick: this.props.onBackButtonClick,
                text: this.state.backButtonText || Resources.BackButtonText
            });
        }

        if (this.props.resourceType === EnvironmentResourceType.VirtualMachine) {
            buttonsProps.push({
                ariaLabel: Resources.CloseText,
                text: Resources.CloseText,
                onClick: this.props.onDismissPanel
            });
            return buttonsProps;
        }

        buttonsProps.push({
            ariaLabel: this.state.forwardButtonText || Resources.CreateButtonText,
            onClick: this.props.onCreateButtonClick,
            text: this.state.forwardButtonText || Resources.CreateButtonText,
            primary: true,
            disabled: this.state.disableActionButton
        });
        return buttonsProps;
    }

    private _onCreateButtonStatusUpdate = (isCreateButtonDisabled: boolean): void => {
        this.setState({ disableActionButton: isCreateButtonDisabled });
    }

    private _setBackButtonText = (text: string): void => {
        this.setState({ backButtonText: text });
    }

    private _setForwardButtonText = (text: string): void => {
        this.setState({ forwardButtonText: text });
    }

    private _updateButtonVisibility = (visible: boolean): void => {
        this.setState({ showButtons: visible });
    }
} 
